import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { formatApiError } from "@/lib/api";
import LanguageSwitcher from "@/components/LanguageSwitcher";

export default function Login() {
  const { login } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const data = await login(email, password);
      if (data.user?.role === "super_admin") navigate("/app/admin");
      else navigate("/app");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[#F8FAFC]">
      {/* Left visual */}
      <div className="hidden lg:flex flex-1 relative overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "url('https://images.pexels.com/photos/93106/pexels-photo-93106.jpeg')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-slate-900/70" />
        <div className="relative z-10 p-16 flex flex-col justify-between text-white">
          <Link to="/" className="flex items-center gap-2" data-testid="login-brand">
            <div className="w-10 h-10 rounded-md bg-white text-[#0052CC] flex items-center justify-center font-bold">GE</div>
            <span className="font-heading font-bold text-lg">{t("app.name")}</span>
          </Link>
          <div>
            <h2 className="font-heading text-4xl font-bold tracking-tight leading-tight max-w-md">
              {t("app.tagline")}
            </h2>
            <p className="mt-4 text-slate-200 max-w-md">
              HubSpot · Salesforce · Pipedrive · Monday — purpose-built for export sales.
            </p>
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex flex-col">
        <div className="flex justify-end p-6">
          <LanguageSwitcher />
        </div>
        <div className="flex-1 flex items-center justify-center px-6">
          <div className="w-full max-w-md">
            <h1 className="font-heading text-3xl font-bold text-slate-900 tracking-tight">
              {t("auth.signin_to_account")}
            </h1>
            <p className="mt-2 text-sm text-slate-600">{t("app.name")}</p>

            <form onSubmit={handleSubmit} className="mt-8 space-y-4" data-testid="login-form">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-700">
                  {t("common.email")}
                </label>
                <input
                  data-testid="login-email-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1.5 w-full px-3 py-2.5 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#0052CC]/40 focus:border-[#0052CC]"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-700">
                  {t("common.password")}
                </label>
                <input
                  data-testid="login-password-input"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-1.5 w-full px-3 py-2.5 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#0052CC]/40 focus:border-[#0052CC]"
                />
              </div>

              {error && (
                <div data-testid="login-error" className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-md">
                  {error}
                </div>
              )}

              <button
                data-testid="login-submit"
                type="submit"
                disabled={loading}
                className="w-full px-4 py-2.5 bg-[#0052CC] text-white rounded-md font-semibold text-sm hover:bg-[#0043A8] transition-colors disabled:opacity-50"
              >
                {loading ? t("common.loading") : t("auth.login")}
              </button>
            </form>

            <div className="mt-6 text-sm text-slate-600">
              {t("auth.no_account")}{" "}
              <Link to="/register" className="font-semibold text-[#0052CC] hover:underline" data-testid="login-to-register">
                {t("auth.register_company")}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
